import { Link } from "wouter";
import { Home, ArrowRight, Phone, Search } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { CONTACT } from "@/lib/constants";

const popularPages = [
  {
    title: "Laserskärning",
    desc: "Precision ner till 0,1 mm i stål, rostfritt och aluminium",
    href: "/tjanster/laserskarning",
  },
  {
    title: "Kantpressning",
    desc: "Bockning med upp till 310 ton presskraft",
    href: "/tjanster/kantpressning",
  },
  {
    title: "Svetsning",
    desc: "Starka fogar för industriella krav",
    href: "/tjanster/svetsning",
  },
  {
    title: "Maskinpark",
    desc: "Vår moderna utrustning och kapacitet",
    href: "/maskinpark",
  },
  {
    title: "Referenser",
    desc: "Projekt för Volvo, försvarsindustrin och fler",
    href: "/referenser",
  },
  {
    title: "Kontakt",
    desc: "Begär offert eller ställ en fråga",
    href: "/kontakt",
  },
];

export default function NotFound() {
  return (
    <Layout>
      <SEOHead
        title="Sidan hittades inte | ASPAB"
        description="Sidan du söker finns inte. Hitta rätt bland våra tjänster inom laserskärning, kantpressning och svetsning."
        path="/404"
        noindex
      />

      <section className="py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-16">
            {/* Large 404 number */}
            <div className="relative inline-block mb-6">
              <span className="font-heading text-[8rem] md:text-[12rem] font-bold leading-none text-gray-100 select-none">
                404
              </span>
              <div className="absolute inset-0 flex items-center justify-center">
                <Search className="w-16 h-16 md:w-20 md:h-20 text-[#2563EB]/40" />
              </div>
            </div>

            <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Sidan hittades inte
            </h1>
            <p className="text-gray-500 text-lg leading-relaxed max-w-xl mx-auto">
              Sidan du letar efter kan ha flyttats, tagits bort eller så finns den inte längre.
              Använd länkarna nedan för att hitta det du söker.
            </p>

            {/* Primary actions */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors"
              >
                <Home className="w-4 h-4" />
                Till startsidan
              </Link>
              <Link
                href="/kontakt"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white border border-gray-200 text-gray-700 font-medium rounded-md hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                <Phone className="w-4 h-4" />
                Kontakta oss
              </Link>
            </div>
          </div>

          {/* Popular pages grid */}
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-xl font-semibold text-gray-900 mb-6 text-center">
              Populära sidor
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {popularPages.map((page) => (
                <Link
                  key={page.href}
                  href={page.href}
                  className="group block p-5 bg-white border border-gray-200 rounded-lg hover:border-[#2563EB]/30 hover:shadow-md transition-all duration-200"
                >
                  <h3 className="font-heading font-semibold text-gray-900 group-hover:text-[#2563EB] transition-colors flex items-center gap-2">
                    {page.title}
                    <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </h3>
                  <p className="text-gray-500 text-sm mt-1.5 leading-relaxed">
                    {page.desc}
                  </p>
                </Link>
              ))}
            </div>
          </div>

          {/* Contact fallback */}
          <div className="max-w-2xl mx-auto mt-14 text-center">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <p className="text-gray-600">
                Hittar du fortfarande inte det du söker? Ring oss på{" "}
                <a
                  href={`tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`}
                  className="text-[#2563EB] font-medium hover:underline"
                >
                  {CONTACT.phone}
                </a>{" "}
                eller skicka e-post till{" "}
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="text-[#2563EB] font-medium hover:underline"
                >
                  {CONTACT.email}
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
